// Per-player HUD panel layout — dock, collapse and size per panel, persisted
// to localStorage under `aod.layout.v1:<uid>` (the Better Auth user id, or
// 'guest' when signed out). Follows the settings-store idiom exactly:
// manual load/persist with try/catch, validate on load, ignore unknown
// shapes (see settings/useSettings.ts).

import { create } from 'zustand';
import {
  DEFAULT_PANEL_LAYOUT,
  GUEST_UID,
  PANEL_IDS,
  type PanelDock,
  type PanelId,
  type PanelLayoutMap,
  clampPanelSize,
  panelStorageKey,
  parseStoredLayout,
} from './layout.js';

interface PanelLayoutStore {
  /** The player the current in-memory layout belongs to and persists under. */
  uid: string;
  panels: PanelLayoutMap;
  /**
   * One-shot identity switch: point persistence at the new uid and apply that
   * uid's stored layout when one exists. A layout already customised this
   * session is never overwritten by an empty stored one — when nothing is
   * stored for the new uid the in-memory layout simply carries over.
   */
  setUser: (uid: string | null) => void;
  setCollapsed: (id: PanelId, collapsed: boolean) => void;
  toggleCollapsed: (id: PanelId) => void;
  /**
   * Pass commit=false from a live drag (avoids a localStorage write per
   * pointermove); the final call commits.
   */
  setSize: (id: PanelId, size: number | null, commit?: boolean) => void;
  setDock: (id: PanelId, dock: PanelDock) => void;
  /** Restore the defaults for the current player (Settings modal button). */
  reset: () => void;
}

function loadStored(uid: string): PanelLayoutMap | null {
  try {
    const raw = localStorage.getItem(panelStorageKey(uid));
    if (!raw) return null;
    return parseStoredLayout(JSON.parse(raw));
  } catch {
    // Corrupt JSON or no localStorage (private mode) — defaults win.
    return null;
  }
}

function persist(uid: string, panels: PanelLayoutMap) {
  try {
    localStorage.setItem(panelStorageKey(uid), JSON.stringify(panels));
  } catch {
    /* quota exceeded — silently skip */
  }
}

function withPanel(
  state: { uid: string; panels: PanelLayoutMap },
  id: PanelId,
  patch: Partial<PanelLayoutMap[PanelId]>,
  commit: boolean,
): { panels: PanelLayoutMap } {
  const panels = { ...state.panels, [id]: { ...state.panels[id], ...patch } };
  if (commit) persist(state.uid, panels);
  return { panels };
}

function defaults(): PanelLayoutMap {
  const out = {} as PanelLayoutMap;
  for (const id of PANEL_IDS) out[id] = { ...DEFAULT_PANEL_LAYOUT[id] };
  return out;
}

export const usePanelLayout = create<PanelLayoutStore>()((set, get) => ({
  uid: GUEST_UID,
  panels: loadStored(GUEST_UID) ?? defaults(),

  setUser: (uid) => {
    const next = uid ?? GUEST_UID;
    if (next === get().uid) return;
    const stored = loadStored(next);
    set(stored ? { uid: next, panels: stored } : { uid: next });
  },

  setCollapsed: (id, collapsed) => set((s) => withPanel(s, id, { collapsed }, true)),

  toggleCollapsed: (id) =>
    set((s) => withPanel(s, id, { collapsed: !s.panels[id].collapsed }, true)),

  setSize: (id, size, commit = true) =>
    set((s) =>
      withPanel(
        s,
        id,
        { size: size === null ? null : clampPanelSize(s.panels[id].dock, size) },
        commit,
      ),
    ),

  setDock: (id, dock) =>
    set((s) => {
      const current = s.panels[id];
      if (current.dock === dock) return s;
      // Join the back of the new dock's stack.
      const maxOrder = Math.max(
        -1,
        ...PANEL_IDS.filter((other) => other !== id && s.panels[other].dock === dock).map(
          (other) => s.panels[other].order,
        ),
      );
      // A dock move also clears a size that was clamped for the old axis
      // (a 240px width is meaningless as a bottom-dock height).
      return withPanel(s, id, { dock, order: maxOrder + 1, size: null }, true);
    }),

  reset: () =>
    set((s) => {
      const panels = defaults();
      persist(s.uid, panels);
      return { panels };
    }),
}));
